<template>
	<div class="ox-viewport">
		<!-- 540x960 세로형 키오스크 보드 (화면에 맞게 스케일) -->
		<div class="ox-fit" :style="{ width: 540 * scale + 'px', height: 960 * scale + 'px' }">
			<main class="ox-kiosk" :style="{ transform: `scale(${scale})` }">
				<!-- 로딩 / 퀴즈 없음 -->
				<section v-if="!loaded || !quizTour" class="ox-screen">
					<div class="ox-center">
						<v-progress-circular v-if="!loaded" indeterminate color="#fff" size="56" width="5" />
						<div v-else class="ox-empty">퀴즈를 찾을 수 없습니다.</div>
					</div>
				</section>

				<section v-else class="ox-screen">
					<header class="ox-header">
						<div class="ox-brand">
							<img :src="logoImg" alt="2026 서울동물행복페스타" />
							<span class="ox-brand-rule"></span>
							<div class="ox-brand-copy">
								<small>동물보호 PLAY존</small>
								<strong>멍쭈의 봉사동물 OX 미션</strong>
							</div>
						</div>
						<div class="ox-header-badge" v-html="headerBadge"></div>
					</header>

					<!-- START -->
					<div v-if="state === 'start'" class="ox-board">
						<div class="ox-start-layout">
							<div class="ox-mission-ribbon">봉사동물 OX 미션</div>
							<h1 class="ox-start-title">멍쭈와 함께하는<br /><span class="ox">봉사동물 OX 퀴즈</span></h1>
							<p class="ox-start-sub">사람과 사회를 돕는 봉사동물을 알아보고<br />올바른 존중과 에티켓을 배워보세요</p>
							<div class="ox-character-stage"><img :src="characterImg" alt="O와 X 팻말을 든 멍쭈" /></div>
							<div class="ox-rule-box">
								<strong
									>총 <span class="num">{{ questions.length }}</span
									>문항</strong
								>
								<span class="divider-dot">◆</span>
								<strong
									><span class="num">{{ passScore }}</span
									>개 이상 정답 시 성공</strong
								>
							</div>
							<button type="button" class="ox-start-btn" @click="startQuiz">START</button>
							<div class="ox-touch-hint">화면을 터치해 미션을 시작하세요</div>
						</div>
					</div>

					<!-- 문제 -->
					<div v-else-if="state === 'playing' && current && !showExplain" class="ox-board ox-quiz-layout">
						<div class="ox-mission-path">
							<div class="ox-path-label">START</div>
							<div class="ox-path-nodes" :style="{ gridTemplateColumns: `repeat(${questions.length}, 1fr)` }">
								<div v-for="(q, qi) in questions" :key="qi" class="ox-path-node" :class="{ done: qi < index, current: qi === index }">
									{{ qi + 1 }}
								</div>
							</div>
							<div class="ox-path-label goal">GOAL</div>
						</div>
						<div class="ox-question-zone">
							<div class="ox-question-meta">
								<div class="ox-question-tag">{{ qLabel }}</div>
								<div class="ox-counter">{{ index + 1 }} / {{ questions.length }}</div>
							</div>
							<div class="ox-question-card">
								<div class="ox-question-text" :class="{ long: (current.question || '').length > 36 }">{{ current.question }}</div>
							</div>
							<div class="ox-answer-guide">정답이라고 생각하는 카드를 터치해주세요</div>
							<div class="ox-answer-row">
								<button type="button" class="ox-answer-btn o" aria-label="O 선택" @click="answer('O')"><span>O</span></button>
								<button type="button" class="ox-answer-btn x" aria-label="X 선택" @click="answer('X')"><span>X</span></button>
							</div>
						</div>
					</div>

					<!-- 판정 + 해설 -->
					<div v-else-if="state === 'playing' && current" class="ox-board">
						<div class="ox-feedback-layout">
							<div class="ox-feedback-top">
								<div class="ox-question-tag">{{ qLabel }}</div>
								<div class="ox-feedback-mini">{{ index + 1 }} / {{ questions.length }}</div>
							</div>
							<div class="ox-feedback-question">{{ current.question }}</div>
							<div class="ox-judge-card">
								<div class="ox-result-mark" :class="isCorrect ? 'correct' : 'wrong'">
									<span>{{ isCorrect ? 'O' : 'X' }}</span>
								</div>
								<div class="ox-result-copy">
									<div class="ox-result-kicker">정답 확인</div>
									<div class="ox-result-title" :class="isCorrect ? 'correct' : 'wrong'">
										{{ isCorrect ? '정답이에요!' : '아쉬워요' }} · 정답 {{ current.ox }}
									</div>
								</div>
							</div>
							<div v-if="current.explain" class="ox-explain-card">
								<div class="ox-explain-label">한 줄 해설</div>
								<div class="ox-explain-text">{{ current.explain }}</div>
							</div>
							<div class="ox-bottom-reaction">
								<div class="ox-reaction-face" :class="isCorrect ? 'correct' : 'wrong'">
									<img :src="isCorrect ? happyImg : sadImg" :alt="isCorrect ? '웃는 멍쭈 얼굴' : '아쉬워하는 멍쭈 얼굴'" />
								</div>
								<div class="ox-reaction-caption">{{ isCorrect ? '멍쭈가 활짝 웃고 있어요' : '멍쭈가 조금 아쉬워하고 있어요' }}</div>
							</div>
							<div class="ox-auto-next">
								잠시 후 다음 문제로 이동합니다
								<div class="ox-count-track"><div :key="'bar' + index" class="ox-count-bar"></div></div>
							</div>
						</div>
					</div>

					<!-- 결과 -->
					<div v-else-if="state === 'result'" class="ox-board">
						<div class="ox-final-layout">
							<div class="ox-final-ribbon" :class="{ fail: !passed }">{{ passed ? '미션 성공' : '다시 도전' }}</div>
							<img class="ox-final-asset" :src="characterResultImg" alt="멍쭈 OX 캐릭터" />
							<h2 class="ox-final-title">{{ passed ? '축하해요! 미션 성공' : '아쉬워요! 조금만 더' }}</h2>
							<!-- 맞힌 개수 / 전체 문항 : 한 줄 가운데 정렬, 숫자 기준선 맞춤 (구분선은 글꼴 대신 CSS 로 그림) -->
							<div class="ox-score-board">
								<span class="score-num">{{ correct }}</span>
								<span class="score-slash" aria-hidden="true"></span>
								<span class="score-total">{{ questions.length }}</span>
								<span class="score-label">문항 정답</span>
							</div>
							<p v-if="passed" class="ox-final-copy">
								멋지게 미션을 완료했어요!<br />현장 담당자의 QR을 스캔하면 PLAY존 <strong>멍쭈</strong> 스탬프가 찍혀요.
							</p>
							<p v-else class="ox-final-copy">정말 아깝게 놓쳤어요!<br />조금만 더 알아보고 다시 도전해보세요.</p>
							<button type="button" class="ox-home-btn" @click="goStart">처음으로 돌아가기</button>
							<div class="ox-final-auto">{{ autoLeft }}초 후 자동으로 첫 화면으로 돌아갑니다</div>
						</div>
					</div>

					<footer class="ox-footer-strip">
						<template v-if="state === 'playing' && showExplain">현재 정답 {{ correct }} / {{ index + 1 }}</template>
						<template v-else-if="state === 'playing'"
							>{{ questions.length }}문항 중 <span class="ox-accent">{{ passScore }}문항 이상 정답</span>이면 미션 성공!</template
						>
						<template v-else-if="state === 'result'">참여해주셔서 감사합니다</template>
						<template v-else>2026 서울동물행복페스타 · 동물보호 PLAY존</template>
					</footer>
				</section>
			</main>
		</div>
	</div>
</template>

<script>
import axios from 'axios'
import logoImg from '@/assets/images/stamp/animal_festa_logo.png'
import characterImg from '@/assets/images/stamp/quiz_ox_character.png'
// 결과 화면용: 녹색 배경 도형을 뺀 캐릭터 (배경이 잘린 블록처럼 보이는 문제)
import characterResultImg from '@/assets/images/stamp/quiz_ox_character_result.png'
import happyImg from '@/assets/images/stamp/quiz_mungjju_happy.png'
import sadImg from '@/assets/images/stamp/quiz_mungjju_sad.png'
import { loadStampUser } from './stampUser'
const BACKEND = process.env.VUE_APP_BACKEND_URL

export default {
	metaInfo() {
		return { title: '봉사동물 OX 퀴즈' }
	},
	data() {
		return {
			scale: 1,
			loaded: false,
			quizTour: null,
			user: null,
			state: 'start', // start | playing | result
			questions: [],
			index: 0,
			correct: 0,
			selected: null,
			showExplain: false,
			passed: false,
			nextTimer: null,
			autoTimer: null,
			autoLeft: 5,
			logoImg,
			characterImg,
			characterResultImg,
			happyImg,
			sadImg,
		}
	},
	computed: {
		groupcode() {
			return this.$route.params.groupcode
		},
		code() {
			return this.$route.params.code
		},
		current() {
			return this.questions[this.index] || null
		},
		isCorrect() {
			return !!this.current && this.selected === this.current.ox
		},
		qLabel() {
			return 'Q ' + String(this.index + 1).padStart(2, '0')
		},
		// 헤더 우측 뱃지 (화면별)
		headerBadge() {
			if (this.state === 'result') return '결과'
			if (this.state === 'playing') return this.showExplain ? '판정<br>확인' : 'OX<br>퀴즈'
			return '미션<br>04'
		},
		passScore() {
			const q = this.quizTour && this.quizTour.quiz
			if (q && q.passScore != null) return Number(q.passScore)
			return Math.ceil(this.questions.length / 2)
		},
	},
	created() {
		// 참여자 식별: 쿼리(phone/name) 우선, 없으면 세션(로그인) 사용
		const q = this.$route.query
		if (q && q.phone) {
			this.user = { phone: q.phone, name: q.name || '' }
		} else {
			const saved = loadStampUser(this.groupcode)
			if (saved) this.user = saved
		}
		this.fetchQuiz()
	},
	mounted() {
		this.updateScale()
		window.addEventListener('resize', this.updateScale)
		if (window.visualViewport) window.visualViewport.addEventListener('resize', this.updateScale)
		window.addEventListener('keydown', this.onKeydown)
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.updateScale)
		if (window.visualViewport) window.visualViewport.removeEventListener('resize', this.updateScale)
		window.removeEventListener('keydown', this.onKeydown)
		clearTimeout(this.nextTimer)
		clearInterval(this.autoTimer)
	},
	methods: {
		updateScale() {
			const vv = window.visualViewport
			const w = vv ? vv.width : window.innerWidth
			const h = vv ? vv.height : window.innerHeight
			this.scale = Math.max(0.1, Math.min(w / 540, h / 960))
		},
		// 키보드 O / X 입력 지원 (문제 화면에서만)
		onKeydown(e) {
			if (this.state !== 'playing' || this.showExplain) return
			const k = (e.key || '').toLowerCase()
			if (k === 'o') this.answer('O')
			if (k === 'x') this.answer('X')
		},
		// 퀴즈 데이터 조회 (phone 없이 호출 → 자동적립 등 부수효과 방지)
		async fetchQuiz() {
			try {
				const res = await axios.get(BACKEND + '/stamp-tours', { params: { groupcode: this.groupcode } })
				if (res.data.result === 'ok') {
					const tour = (res.data.tours || []).find(t => t.type === 'quiz' && t.code === this.code)
					if (tour) {
						this.quizTour = tour
						const quiz = tour.quiz || {}
						let list = Array.isArray(quiz.questions) ? quiz.questions.slice() : []
						if (quiz.shuffle) list = this.shuffle(list)
						this.questions = list
					}
				}
			} catch (err) {
				console.log({ err })
			} finally {
				this.loaded = true
			}
		},
		shuffle(arr) {
			const a = arr.slice()
			for (let i = a.length - 1; i > 0; i--) {
				const j = Math.floor(Math.random() * (i + 1))
				;[a[i], a[j]] = [a[j], a[i]]
			}
			return a
		},
		startQuiz() {
			if (this.questions.length === 0) return
			// 셔플 재적용 (재도전 시 순서 다시 섞기)
			const quiz = this.quizTour.quiz || {}
			if (quiz.shuffle) this.questions = this.shuffle(this.questions)
			this.index = 0
			this.correct = 0
			this.selected = null
			this.showExplain = false
			this.state = 'playing'
		},
		answer(ox) {
			if (this.showExplain || !this.current) return
			this.selected = ox
			if (ox === this.current.ox) this.correct++
			this.showExplain = true
			clearTimeout(this.nextTimer)
			this.nextTimer = setTimeout(() => this.next(), 3000)
		},
		next() {
			if (this.index + 1 >= this.questions.length) {
				this.finish()
				return
			}
			this.index++
			this.selected = null
			this.showExplain = false
		},
		finish() {
			this.passed = this.correct >= this.passScore
			this.state = 'result'
			// 자동 스탬프 적립은 진행하지 않음 (현장에서 별도 확인)
			// 5초 카운트다운 후 처음으로
			this.autoLeft = 5
			clearInterval(this.autoTimer)
			this.autoTimer = setInterval(() => {
				this.autoLeft--
				if (this.autoLeft <= 0) {
					clearInterval(this.autoTimer)
					this.goStart()
				}
			}, 1000)
		},
		goStart() {
			clearInterval(this.autoTimer)
			clearTimeout(this.nextTimer)
			this.state = 'start'
			this.index = 0
			this.correct = 0
			this.selected = null
			this.showExplain = false
		},
	},
}
</script>

<style>
@import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/variable/pretendardvariable-dynamic-subset.min.css');
</style>

<style lang="scss" scoped>
$blue: #2c95cf;
$blue-dark: #14689e;
$yellow: #ffd24e;
$orange: #ef6b18;
$green: #2da65a;
$red: #e93c3b;
$ink: #111f30;

.ox-viewport {
	position: fixed;
	inset: 0;
	background: #0d1726;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	color: $ink;
	font-family: 'Pretendard Variable', 'Pretendard', 'Apple SD Gothic Neo', 'Noto Sans KR', system-ui, -apple-system, sans-serif;
	font-synthesis: none;
	touch-action: manipulation;
	* {
		box-sizing: border-box;
		-webkit-tap-highlight-color: transparent;
	}
	button {
		font-family: inherit;
		cursor: pointer;
	}
	h1,
	h2,
	p {
		margin: 0;
	}
}
.ox-fit {
	position: relative;
	overflow: hidden;
	flex: none;
}
// 정확히 540 x 960 으로 그린 뒤 뷰포트에 맞춰 스케일
.ox-kiosk {
	position: absolute;
	left: 0;
	top: 0;
	width: 540px;
	height: 960px;
	overflow: hidden;
	background: $blue;
	transform-origin: top left;
	box-shadow: 0 28px 70px rgba(0, 0, 0, 0.38);
}
.ox-screen {
	position: absolute;
	inset: 0;
	display: flex;
	flex-direction: column;
	background: $blue;
	// 배경 원형 장식
	&::before,
	&::after {
		content: '';
		position: absolute;
		border-radius: 50%;
		background: rgba(255, 255, 255, 0.07);
		pointer-events: none;
	}
	&::before {
		width: 220px;
		height: 220px;
		left: -90px;
		top: 160px;
	}
	&::after {
		width: 160px;
		height: 160px;
		right: -74px;
		bottom: 130px;
	}
}
.ox-center {
	flex: 1;
	display: grid;
	place-items: center;
}
.ox-empty {
	color: #fff;
	font-size: 22px;
	font-weight: 800;
}
// 헤더
.ox-header {
	height: 78px;
	flex: 0 0 auto;
	background: #fff;
	border-bottom: 5px solid $blue-dark;
	display: flex;
	align-items: center;
	padding: 0 22px;
	gap: 12px;
	position: relative;
	z-index: 5;
}
.ox-brand {
	display: flex;
	align-items: center;
	min-width: 0;
	flex: 1;
	gap: 11px;
	img {
		width: 116px;
		height: 55px;
		object-fit: contain;
		display: block;
	}
}
.ox-brand-rule {
	width: 2px;
	height: 34px;
	background: #d7e5ee;
	flex: 0 0 auto;
}
.ox-brand-copy {
	line-height: 1.05;
	min-width: 0;
	small {
		display: block;
		color: $orange;
		font-size: 11px;
		font-weight: 700;
		letter-spacing: 0.02em;
		margin-bottom: 4px;
	}
	strong {
		display: block;
		font-size: 14px;
		font-weight: 800;
		letter-spacing: -0.045em;
		white-space: nowrap;
	}
}
.ox-header-badge {
	flex: 0 0 auto;
	background: #f5fbfe;
	border: 2px solid #c9e2ef;
	color: $blue-dark;
	border-radius: 12px;
	padding: 7px 10px;
	font-size: 12px;
	font-weight: 700;
	line-height: 1.1;
	text-align: center;
}
// 보드
.ox-board {
	flex: 1;
	min-height: 0;
	margin: 14px;
	border: 5px solid $blue-dark;
	border-radius: 34px 34px 28px 28px;
	background: #fff;
	position: relative;
	z-index: 2;
	overflow: hidden;
	box-shadow: 0 11px 0 rgba(19, 88, 134, 0.1), 0 18px 34px rgba(22, 71, 104, 0.14);
	&::before {
		content: '';
		position: absolute;
		inset: 0;
		background-image: radial-gradient(circle at 12px 12px, rgba(44, 149, 207, 0.07) 0 2px, transparent 2.4px);
		background-size: 30px 30px;
		pointer-events: none;
	}
}
.ox-footer-strip {
	height: 44px;
	flex: 0 0 auto;
	margin: 0 14px 14px;
	background: #fff;
	border: 2px solid #b9d6e6;
	border-radius: 16px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
	font-weight: 600;
	color: #64768a;
	position: relative;
	z-index: 4;
	.ox-accent {
		color: $orange;
		margin: 0 3px;
	}
}
// START
.ox-start-layout {
	height: 100%;
	padding: 24px 28px 20px;
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	position: relative;
}
.ox-mission-ribbon {
	position: relative;
	display: inline-flex;
	align-items: center;
	background: $orange;
	color: #fff;
	padding: 8px 16px;
	border-radius: 999px;
	font-size: 13px;
	font-weight: 700;
	letter-spacing: -0.02em;
	z-index: 2;
}
.ox-start-title {
	margin-top: 18px;
	font-size: 39px;
	line-height: 1.02;
	font-weight: 800;
	letter-spacing: -0.06em;
	position: relative;
	z-index: 2;
	.ox {
		color: $blue;
		font-size: 1.18em;
	}
}
.ox-start-sub {
	margin-top: 10px !important;
	font-size: 15px;
	font-weight: 600;
	color: #5d7084;
	line-height: 1.45;
	letter-spacing: -0.02em;
	position: relative;
	z-index: 2;
}
.ox-character-stage {
	margin-top: 12px;
	width: 310px;
	height: 270px;
	position: relative;
	display: grid;
	place-items: center;
	z-index: 2;
	&::before {
		content: '';
		position: absolute;
		width: 222px;
		height: 242px;
		background: #498d49;
		clip-path: polygon(50% 0, 93% 24%, 93% 75%, 50% 100%, 7% 75%, 7% 24%);
		filter: drop-shadow(0 9px 0 rgba(20, 103, 158, 0.14));
	}
	&::after {
		content: 'OX';
		position: absolute;
		right: 1px;
		top: 22px;
		width: 62px;
		height: 62px;
		border: 4px solid $blue-dark;
		border-radius: 18px;
		background: $yellow;
		display: grid;
		place-items: center;
		color: $blue-dark;
		font-weight: 900;
		font-size: 20px;
		transform: rotate(7deg);
		box-shadow: 5px 5px 0 #fff;
	}
	img {
		position: relative;
		width: 292px;
		height: 252px;
		object-fit: contain;
		filter: drop-shadow(0 9px 8px rgba(12, 48, 69, 0.12));
	}
}
.ox-rule-box {
	width: 100%;
	max-width: 424px;
	display: grid;
	grid-template-columns: 1fr auto 1fr;
	align-items: center;
	gap: 10px;
	background: #fff7d5;
	border: 3px solid #edc440;
	border-radius: 18px;
	padding: 12px 15px;
	position: relative;
	z-index: 2;
	strong {
		font-size: 16px;
		font-weight: 900;
		color: #4b3c05;
	}
	.num {
		font-size: 28px;
		color: $blue-dark;
	}
	.divider-dot {
		font-size: 20px;
		color: $orange;
		font-weight: 900;
	}
}
.ox-start-btn {
	margin-top: 16px;
	width: 100%;
	max-width: 424px;
	height: 68px;
	border: 0;
	border-radius: 16px;
	background: $blue-dark;
	color: #fff;
	font-size: 27px;
	font-weight: 800;
	letter-spacing: 0.08em;
	box-shadow: 0 7px 0 #0b507a, 0 12px 18px rgba(20, 104, 158, 0.2);
	position: relative;
	z-index: 2;
	&:active {
		transform: translateY(4px);
		box-shadow: 0 3px 0 #0b507a, 0 8px 12px rgba(20, 104, 158, 0.16);
	}
}
.ox-touch-hint {
	margin-top: 12px;
	font-size: 12px;
	font-weight: 800;
	color: #8291a1;
	position: relative;
	z-index: 2;
}
// 진행 경로
.ox-mission-path {
	display: grid;
	grid-template-columns: 72px 1fr 62px;
	align-items: center;
	gap: 9px;
	padding: 16px 20px 12px;
	position: relative;
	z-index: 3;
}
.ox-path-label {
	font-size: 11px;
	font-weight: 700;
	color: $blue-dark;
	background: #fff;
	border: 2px solid $blue-dark;
	border-radius: 10px;
	padding: 6px 5px;
	text-align: center;
}
.ox-path-nodes {
	display: grid;
	align-items: center;
	position: relative;
	&::before {
		content: '';
		position: absolute;
		left: 6%;
		right: 6%;
		top: 50%;
		height: 4px;
		background: #cce2ef;
		transform: translateY(-50%);
		z-index: 0;
		border-radius: 99px;
	}
}
.ox-path-node {
	position: relative;
	z-index: 1;
	width: 24px;
	height: 24px;
	margin: auto;
	border-radius: 8px;
	background: #fff;
	border: 3px solid #b9d7e8;
	color: #7890a4;
	display: grid;
	place-items: center;
	font-size: 9px;
	font-weight: 700;
	transition: 0.2s;
	&.done {
		background: $blue;
		border-color: $blue;
		color: #fff;
	}
	&.current {
		background: $yellow;
		border-color: $orange;
		color: #593400;
		transform: scale(1.18) rotate(-3deg);
		box-shadow: 0 3px 0 rgba(239, 107, 24, 0.25);
	}
}
// 문제
.ox-quiz-layout {
	display: flex;
	flex-direction: column;
}
.ox-question-zone {
	padding: 4px 20px 0;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	position: relative;
	z-index: 2;
}
.ox-question-meta {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin: 0 2px 9px;
}
.ox-question-tag {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	background: $orange;
	color: #fff;
	border-radius: 10px;
	padding: 7px 11px;
	font-size: 13px;
	font-weight: 700;
	&::before {
		content: '?';
		display: grid;
		place-items: center;
		width: 20px;
		height: 20px;
		border-radius: 50%;
		background: #fff;
		color: $orange;
		font-size: 13px;
	}
}
.ox-counter {
	font-size: 17px;
	font-weight: 700;
	color: $blue-dark;
}
.ox-question-card {
	flex: 1;
	min-height: 260px;
	max-height: 326px;
	background: #fffdf7;
	border: 2px solid #bdd5e3;
	border-radius: 22px;
	padding: 28px 27px;
	display: grid;
	place-items: center;
	text-align: center;
	box-shadow: 0 5px 0 #e8f1f6;
	position: relative;
}
.ox-question-text {
	font-size: 31px;
	line-height: 1.38;
	font-weight: 800;
	letter-spacing: -0.055em;
	word-break: keep-all;
	max-width: 100%;
	&.long {
		font-size: 27px;
	}
}
.ox-answer-guide {
	margin: 14px 0 8px;
	text-align: center;
	color: #718293;
	font-size: 12px;
	font-weight: 600;
}
.ox-answer-row {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;
	height: 176px;
	flex: 0 0 auto;
}
.ox-answer-btn {
	position: relative;
	border-radius: 20px;
	background: #fff;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 86px;
	font-weight: 900;
	transition: 0.12s;
	span {
		transform: translateY(-2px);
	}
	&.o {
		border: 5px solid $green;
		color: $green;
		box-shadow: 0 4px 0 #227d45;
	}
	&.x {
		border: 5px solid $red;
		color: $red;
		box-shadow: 0 4px 0 #b02d2a;
	}
	&:active {
		transform: translateY(5px);
		box-shadow: 0 2px 0 rgba(0, 0, 0, 0.14);
	}
}
// 판정 + 해설
.ox-feedback-layout {
	height: 100%;
	padding: 16px 20px 20px;
	display: flex;
	flex-direction: column;
	position: relative;
	z-index: 2;
}
.ox-feedback-top {
	display: flex;
	align-items: center;
	justify-content: space-between;
}
.ox-feedback-mini {
	font-size: 13px;
	font-weight: 700;
	color: $blue-dark;
}
.ox-feedback-question {
	margin-top: 10px;
	padding: 15px 17px;
	background: #fbfdff;
	border: 2px solid #d7e5ed;
	border-radius: 16px;
	font-size: 18px;
	line-height: 1.42;
	font-weight: 700;
	color: #25364a;
	letter-spacing: -0.035em;
	text-align: center;
	word-break: keep-all;
}
.ox-judge-card {
	margin-top: 15px;
	border: 2px solid #d9e5ec;
	border-radius: 22px;
	background: #fff;
	display: grid;
	grid-template-columns: 112px 1fr;
	align-items: center;
	gap: 16px;
	padding: 14px 18px;
}
.ox-result-mark {
	width: 100px;
	height: 100px;
	border-radius: 22px;
	display: grid;
	place-items: center;
	font-weight: 900;
	font-size: 58px;
	border: 5px solid #fff;
	outline: 4px solid currentColor;
	span {
		color: #fff;
		line-height: 1;
	}
	&.correct {
		background: $green;
		color: $green;
	}
	&.wrong {
		background: $red;
		color: $red;
	}
}
.ox-result-copy {
	text-align: left;
	min-width: 0;
}
.ox-result-kicker {
	font-size: 11px;
	color: #7a8998;
	font-weight: 700;
	margin-bottom: 4px;
}
.ox-result-title {
	font-size: 24px;
	font-weight: 800;
	letter-spacing: -0.045em;
	line-height: 1.18;
	&.correct {
		color: $green;
	}
	&.wrong {
		color: $red;
	}
}
.ox-explain-card {
	margin-top: 15px;
	background: #fffaf0;
	border: 1px solid #eadba1;
	border-radius: 20px;
	padding: 17px 18px;
}
.ox-explain-label {
	color: #e66a14;
	font-size: 12px;
	font-weight: 700;
	margin-bottom: 8px;
}
.ox-explain-text {
	font-size: 18px;
	line-height: 1.45;
	font-weight: 600;
	color: #26374a;
	letter-spacing: -0.03em;
	word-break: keep-all;
}
// 멍쭈 반응 (해설 하단)
.ox-bottom-reaction {
	margin-top: 18px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 9px;
}
.ox-reaction-face {
	width: 170px;
	height: 170px;
	border-radius: 999px;
	overflow: hidden;
	position: relative;
	display: grid;
	place-items: center;
	background: #fff;
	border: 4px solid #dbe7ed;
	box-shadow: 0 7px 0 rgba(34, 85, 123, 0.09);
	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}
	&.correct {
		border-color: #b9dcc5;
	}
	&.wrong {
		border-color: #f1c9c7;
		// 눈물 방울
		&::after {
			content: '';
			position: absolute;
			right: 27px;
			top: 84px;
			width: 16px;
			height: 24px;
			background: #4aaee1;
			border-radius: 70% 30% 65% 35%;
			transform: rotate(38deg);
			box-shadow: 0 1px 0 rgba(0, 0, 0, 0.08);
		}
	}
}
.ox-reaction-caption {
	font-size: 13px;
	line-height: 1.3;
	font-weight: 700;
	color: #718196;
	text-align: center;
}
.ox-auto-next {
	margin-top: auto;
	padding-top: 12px;
	text-align: center;
	font-size: 12px;
	font-weight: 600;
	color: #718196;
}
.ox-count-track {
	margin-top: 8px;
	height: 7px;
	border-radius: 99px;
	background: #e4edf3;
	overflow: hidden;
}
.ox-count-bar {
	height: 100%;
	width: 100%;
	background: $blue;
	transform-origin: left center;
	animation: oxCountdown 3s linear forwards;
}
@keyframes oxCountdown {
	to {
		transform: scaleX(0);
	}
}
// 결과
.ox-final-layout {
	height: 100%;
	padding: 20px 28px;
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	position: relative;
	z-index: 2;
}
.ox-final-ribbon {
	background: $yellow;
	color: $blue-dark;
	border: 2px solid #c4dce8;
	border-radius: 14px;
	padding: 8px 16px;
	font-size: 15px;
	font-weight: 700;
	&.fail {
		background: #ffe5e3;
		color: #b92727;
	}
}
.ox-final-asset {
	width: 250px;
	height: 220px;
	object-fit: contain;
	margin-top: 8px;
	filter: drop-shadow(0 8px 7px rgba(16, 64, 92, 0.12));
}
.ox-final-title {
	margin-top: 5px !important;
	font-size: 36px;
	line-height: 1.05;
	font-weight: 800;
	letter-spacing: -0.055em;
}
.ox-score-board {
	margin-top: 13px;
	display: flex;
	justify-content: center;
	align-items: baseline;
	width: 100%;
	max-width: 390px;
	background: #fbfdff;
	border: 2px solid #c9dde8;
	border-radius: 18px;
	padding: 12px 16px;
	line-height: 1;
	font-variant-numeric: tabular-nums;
	.score-num {
		font-size: 44px;
		color: $blue-dark;
		font-weight: 800;
	}
	// 글꼴 '/' 대신 얇은 사선 막대 → 기기/폰트와 무관하게 깔끔하게 보이도록
	.score-slash {
		align-self: center;
		width: 3px;
		height: 30px;
		margin: 0 14px;
		border-radius: 2px;
		background: #b3c2cf;
		transform: rotate(20deg);
	}
	.score-total {
		font-size: 30px;
		color: #4f6378;
		font-weight: 800;
	}
	.score-label {
		margin-left: 8px;
		font-size: 18px;
		color: #6b7d8f;
		font-weight: 700;
	}
}
.ox-final-copy {
	margin-top: 13px !important;
	font-size: 16px;
	line-height: 1.45;
	font-weight: 600;
	color: #5e6f81;
	word-break: keep-all;
}
.ox-home-btn {
	margin-top: auto;
	width: 100%;
	max-width: 400px;
	height: 62px;
	border: 0;
	border-radius: 15px;
	background: $blue-dark;
	color: #fff;
	font-size: 22px;
	font-weight: 800;
	box-shadow: 0 6px 0 #0a4d77;
}
.ox-final-auto {
	margin-top: 9px;
	color: #7f8d9c;
	font-size: 11px;
	font-weight: 600;
}
@media (prefers-reduced-motion: reduce) {
	.ox-viewport * {
		animation: none !important;
		transition: none !important;
	}
}
</style>
